import { getHours, setHours, getMinutes, setMinutes } from 'date-fns'
import { ColorScheme } from 'src/components/CoinChart/config'
import { ChartFormat } from 'src/components/TimeLineChart/components/ChartFormat'
import {
  formatGlobalChartCurrency,
  formatCurrency2String,
} from 'src/utils/number/currency'

const rounding = (value: number, count: number) => {
  const mod = value % count
  return mod > count / 2 ? value + (count - mod) : value - mod
}

const roundTimestampToHours = (date: Date, count: number) =>
  setHours(setMinutes(date, 0), rounding(getHours(date), count))

const roundTimestampToMinutes = (date: Date, count: number) =>
  setMinutes(date, rounding(getMinutes(date), count))

type periodNumberMap = {
  [K in string]: number
}

type periodStringMap = {
  [K in string]: string
}

type periodDateRoundMap = {
  [K in string]: (date: Date, count: number) => Date
}

const tickPixelIntervalByPeriod: periodNumberMap = {
  '5m': 200,
  '15m': 200,
  '1h': 200,
  '2h': 200,
  '24h': 200,
}

const dateMinRangeByPeriod: periodNumberMap = {
  '5m': 3600 * 1000 * 2,
  '15m': 3600 * 1000 * 6,
  '30m': 3600 * 1000 * 12,
  '1h': 3600 * 1000 * 24,
  '2h': 3600 * 1000 * 48,
  '24h': 3600 * 1000 * 72,
  '7d': 3600 * 1000 * 24 * 30,
  '30d': 3600 * 1000 * 90,
}

const dateLabelFormatByPeriod: periodStringMap = {
  '5m': 'LLL dd HH:mm',
  '15m': 'LLL dd HH:mm',
  '1h': 'LLL dd',
  '2h': 'LLL yy',
  '24h': 'LLL yy',
}

const dateRoundingFuncByPeriod: periodDateRoundMap = {
  '5m': roundTimestampToMinutes,
  '15m': roundTimestampToMinutes,
  '1h': roundTimestampToHours,
  '2h': roundTimestampToHours,
  '24h': roundTimestampToHours,
}

const dateRoundingCountByPeriod: periodNumberMap = {
  '5m': 5,
  '15m': 15,
  '1h': 1,
  '2h': 2,
  '24h': 24,
}

export const getDateTickPixelInterval = (period: string) =>
  tickPixelIntervalByPeriod[period] || 100

export const getDateMinRange = (period: string) => dateMinRangeByPeriod[period]

export const getDateLabelFormat = (period: string) =>
  dateLabelFormatByPeriod[period] || "LLL ''yy"

export const formatCurrency = (number: number) =>
  formatGlobalChartCurrency(number, false, true)

export const formatTooltipCurrency = (number: number) =>
  formatCurrency2String(number, true)

export const roundDate = (period: string, date: Date) => {
  const f = dateRoundingFuncByPeriod[period]
  return f ? f(date, dateRoundingCountByPeriod[period]) : date
}

export const getDateTooltipFormat = () => "EEEE, dd LLL yyyy, HH:mm 'UTC'xxx"

type ColorsConfigMap = {
  [TKey in string]: {
    primary: string
    secondary: string
    bground: string
  }
}

interface Colors {
  primary: string
  secondary: string
  bground: string
}

const STYLES: ColorsConfigMap = {
  [ColorScheme.BRIGHT]: {
    primary: 'rgba(43, 43, 43, 0.7)',
    secondary: 'rgba(43, 43, 43, 0.4)',
    bground: 'rgba(0, 0, 0, 0.05)',
  },
  [ColorScheme.DARK]: {
    primary: '#fff',
    secondary: 'rgba(255, 255, 255, 0.4)',
    bground: 'rgba(255, 255, 255, 0.05)',
  },
}

function titleLayout(chartFormat: ChartFormat): string {
  const width = chartFormat == ChartFormat.VERTICAL ? '400' : '1100'
  return `margin-left: 10px; margin-top:20px; width:${width}px;`
}

function dateRangeCss(chartFormat: ChartFormat, colors: Colors): string {
  const display =
    chartFormat == ChartFormat.VERTICAL
      ? 'display:block;margin-top:10px'
      : 'display:inline-block'
  return (
    `${display};width:300px;font-size:15px;line-height:18px;float:right;` +
    `color:${colors.primary};background-color:${colors.bground};padding:10px;border-radius:2px;`
  )
}

function titleCss(colors: Colors): string {
  return `font-size:24px;line-height:28px;color:${colors.primary};font-family:&quot;Roboto&quot;,sans-serif;text-transform:capitalize;`
}

export const getExportTitleConfig = (
  from: string,
  to: string,
  scheme: string,
  chartFormat: ChartFormat
) => {
  const colors = STYLES[scheme]
  return {
    /* eslint-disable */
    text: `
      <div style="${titleLayout(chartFormat)}">
        <span style="${titleCss(colors)}">Total Market Capitalization</span>
        <span style="${dateRangeCss(chartFormat, colors)}">
          <span style="color:${colors.secondary};">From</span>&nbsp;${from}<span style="color:${
      colors.secondary
    };">&nbsp;-&nbsp;To&nbsp;</span>${to}
        </span>
      </div>`,
    /* eslint-enable */
    align: 'left',
    useHTML: true,
  }
}
